import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../apiClient';
// The drill-down's milestone markers, so a status draws identically on both pages.
import { MilestoneGlyph } from './ProjectSchedule';
import { projectPath } from './projectDeepLink';

// ─── PORTFOLIO TIMELINE ──────────────────────────────────────────────────────
// One row per project across the whole delivery portfolio, from `GET /portfolio/timeline`. Replaced
// a hardcoded sample-data mock of the leadership "Portfolio Go-Live Roadmap" deck (2026-08-26).
//
// Every derived value — the go-live date and where it came from, each workstream's span, rolled-up
// progress — is computed server-side in PortfolioService, not here. Two reasons: the axis has to be
// the same for every row, and a future export or Dashboard rollup must not re-derive "go-live" its
// own slightly different way.
//
// Bars are positioned as percentages of the window rather than in grid columns (which is what the
// mock did over a fixed 24 months): real spans run 2020 to 2028, so the column count is data, and a
// percentage track reflows at any zoom without recomputing a template.

/** Left-hand columns, in order. Sticky as one block, so the widths have to be shared. */
const COLUMNS = [
  { key: 'state', label: 'State', width: 52, sortBy: (r) => r.state || '' },
  { key: 'name', label: 'Project', width: 232, sortBy: (r) => r.name },
  { key: 'status', label: 'Status', width: 116, sortBy: (r) => r.projectStatus },
  { key: 'goLive', label: 'Go-Live', width: 104, sortBy: (r) => dayValue(r.goLive?.date) },
  { key: 'progress', label: 'Progress', width: 92, sortBy: (r) => r.progress?.pct ?? -1 },
];

const LABEL_WIDTH = COLUMNS.reduce((sum, c) => sum + c.width, 0);

/**
 * Enough width that a multi-year window stays readable; the track scrolls rather than compressing.
 * Also what keeps the quarter ticks legible — 15px a month is 45px a quarter at any zoom.
 */
const MIN_MONTH_WIDTH = 15;

const ROW_HEIGHT = 40;
const BAR_HEIGHT = 20;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Mirrors App.js's gate: the drill-down link would 404 on a route that only exists when the flag is
// on. Read here rather than passed down, same as App.js does it.
const SCHEDULING_ENABLED = process.env.REACT_APP_SCHEDULING_ENABLED === 'true';

// Narrowest first, and 12 months is the default: the near-term view is the one people open this page
// for, and "All" spans 2020→2028, which is only readable after scrolling.
const ZOOMS = [
  { key: '12m', label: '12 months' },
  { key: '24m', label: '24 months' },
  { key: 'all', label: 'All' },
];

const DEFAULT_ZOOM = '12m';

/**
 * How each project status reads on the row.
 *
 * Keyed on Project.projectStatus (PROJECT_STATUS_VALUES in apps/api), not on a status vocabulary of
 * this page's own — the mock invented live/on-track/awarded, none of which exist in the data.
 */
const STATUS_STYLE = {
  Placeholder: { badge: 'badge-violet', bar: 'var(--violet)' },
  Startup: { badge: 'badge-blue', bar: 'var(--primary-light)' },
  Active: { badge: 'badge-blue', bar: 'var(--primary-dark)' },
  'Final Acceptance': { badge: 'badge-green', bar: 'var(--teal)' },
  Converted: { badge: 'badge-gray', bar: 'var(--gray)' },
  Completed: { badge: 'badge-green', bar: 'var(--green)' },
  Archived: { badge: 'badge-gray', bar: 'var(--gray)' },
};

const RAG_COLOR = { Red: 'var(--red)', Amber: 'var(--yellow)', Green: 'var(--green)' };

/** What each go-live source means, shown next to the date so a derived one is never read as entered. */
const GO_LIVE_SOURCE = {
  milestone: { mark: '', hint: 'From the milestone flagged as go-live on this project' },
  targetEndDate: { mark: '~', hint: 'No go-live milestone flagged — using the project target end date' },
  scheduleEnd: { mark: '~', hint: 'No go-live milestone or target end date — using the last dated row in the schedule' },
};

const PROGRESS_SOURCE_HINT = {
  tasks: 'Weighted by task duration across the promoted schedule',
  milestones: 'Share of milestones delivered or accepted — this project has no promoted schedule',
};

// ─── DATES ───────────────────────────────────────────────────────────────────

/**
 * Every date the API returns is a UTC-midnight timestamp (ADR 0009). Read it back with UTC getters
 * and rebuild it as a local date, so both formatting and positioning land on the day that was
 * stored — `new Date(value)` alone renders Apr 26 as Apr 25 anywhere west of UTC.
 */
function toDay(value) {
  if (!value) return null;
  const d = new Date(value);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function dayValue(value) {
  const d = toDay(value);
  return d ? +d : Number.POSITIVE_INFINITY;
}

function fmtMonthYear(value) {
  const d = toDay(value);
  return d ? `${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '—';
}

function fmtFullDate(value) {
  const d = toDay(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function startOfToday() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

// ─── WINDOW ──────────────────────────────────────────────────────────────────

/**
 * The axis: a start/end pair plus the year and quarter ticks drawn against it.
 *
 * Snapped to whole months so the ticks line up with their labels, and padded a month either side so
 * a bar that ends on the window's last day isn't flush against the edge.
 */
function buildWindow(dataWindow, zoom) {
  const today = startOfToday();
  let start;
  let end;

  if (zoom === '12m') {
    start = addMonths(today, -1);
    end = addMonths(today, 11);
  } else if (zoom === '24m') {
    start = addMonths(today, -3);
    end = addMonths(today, 21);
  } else {
    const from = toDay(dataWindow?.start) ?? addMonths(today, -6);
    const to = toDay(dataWindow?.end) ?? addMonths(today, 18);
    start = addMonths(new Date(from.getFullYear(), from.getMonth(), 1), -1);
    end = addMonths(new Date(to.getFullYear(), to.getMonth(), 1), 2);
  }

  const span = +end - +start;
  const monthCount = Math.max(
    1,
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()),
  );

  const pctOf = (date) => {
    const d = toDay(date);
    if (!d) return null;
    return ((+d - +start) / span) * 100;
  };

  // Ticks are generated rather than laid out in a grid, because the window can start mid-year.
  const years = [];
  const quarters = [];
  for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
    const yearStart = new Date(y, 0, 1);
    const yearEnd = new Date(y + 1, 0, 1);
    const left = Math.max(pctOf(yearStart), 0);
    const right = Math.min(pctOf(yearEnd), 100);
    if (right > 0 && left < 100) years.push({ year: y, left, width: right - left });

    for (let q = 0; q < 4; q++) {
      const qStart = new Date(y, q * 3, 1);
      const qEnd = new Date(y, q * 3 + 3, 1);
      const qLeft = Math.max(pctOf(qStart), 0);
      const qRight = Math.min(pctOf(qEnd), 100);
      if (qRight > 0 && qLeft < 100) {
        quarters.push({ key: `${y}-q${q + 1}`, label: `Q${q + 1}`, left: qLeft, width: qRight - qLeft });
      }
    }
  }

  return {
    start,
    end,
    monthCount,
    minWidth: monthCount * MIN_MONTH_WIDTH,
    years,
    quarters,
    pctOf,
    // Clamped, for bars whose real dates fall outside the current zoom — the bar still has to be
    // drawn, cut off at the edge, rather than positioned off-screen.
    clamp: (date) => {
      const pct = pctOf(date);
      return pct === null ? null : Math.min(Math.max(pct, 0), 100);
    },
    todayPct: pctOf(today),
  };
}

/**
 * Where to scroll the track so today sits in the middle of the visible timeline.
 *
 * Matters most on the "All" zoom, whose window spans 2020→2028: unscrolled, it opens on 2020 and
 * every current project is off-screen to the right.
 *
 * A near-copy of ProjectSchedule's `centerScrollLeft` rather than a shared import — that one closes
 * over its own page's LABEL_WIDTH, and this page's label block is more than twice as wide, so the
 * only honest way to reuse it would be to change its signature and its tests.
 */
export function centerTodayScrollLeft(todayPct, scrollWidth, clientWidth, labelWidth = LABEL_WIDTH) {
  // Out-of-window dates clamp to 0/100, which would slam the track to one end and read as a bug.
  if (todayPct === null || todayPct <= 0 || todayPct >= 100) return null;
  // An unlaid-out or hidden table reports clientWidth 0; centering against a viewport narrower than
  // the frozen columns is not a scroll position anyone wants.
  if (!(clientWidth > labelWidth) || !(scrollWidth > clientWidth)) return null;
  const todayX = labelWidth + ((scrollWidth - labelWidth) * todayPct) / 100;
  const visibleTimeline = clientWidth - labelWidth;
  return Math.max(0, Math.min(scrollWidth - clientWidth, todayX - labelWidth - visibleTimeline / 2));
}

// ─── SMALL PIECES ────────────────────────────────────────────────────────────

function SortTh({ column, sort, onSort }) {
  const active = sort.key === column.key;
  return (
    <button
      type="button"
      onClick={() => onSort(column.key)}
      title={`Sort by ${column.label}`}
      style={{
        width: column.width,
        flexShrink: 0,
        background: 'none',
        border: 'none',
        padding: '0 6px 0 0',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: active ? 'var(--primary-light)' : 'var(--text-mid)',
      }}
    >
      {column.label}
      <span style={{ marginLeft: 3, opacity: active ? 1 : 0.25 }}>
        {active && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </button>
  );
}

function TimelineAxis({ view }) {
  return (
    <div style={{ position: 'relative', height: 30 }}>
      {view.years.map((y) => (
        <div
          key={y.year}
          style={{
            position: 'absolute',
            left: `${y.left}%`,
            width: `${y.width}%`,
            top: 0,
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-mid)',
            textAlign: 'center',
            borderLeft: '1px solid var(--border)',
            overflow: 'hidden',
          }}
        >
          {y.year}
        </div>
      ))}
      {view.quarters.map((q) => (
        <div
          key={q.key}
          style={{
            position: 'absolute',
            left: `${q.left}%`,
            width: `${q.width}%`,
            top: 15,
            fontSize: 9,
            color: 'var(--text-light)',
            textAlign: 'center',
            borderLeft: '1px solid var(--border)',
            overflow: 'hidden',
          }}
        >
          {q.label}
        </div>
      ))}
    </div>
  );
}

/** Faint year gridlines behind every row, so a bar's position is readable without a ruler. */
function GridLines({ view }) {
  return (
    <>
      {view.years.map((y) => (
        <div
          key={y.year}
          style={{
            position: 'absolute',
            left: `${y.left}%`,
            top: 0,
            bottom: 0,
            borderLeft: '1px solid var(--border)',
            opacity: 0.6,
            pointerEvents: 'none',
          }}
        />
      ))}
    </>
  );
}

/**
 * The today marker: Aumentum gold, dashed, drawn with a border rather than a background so the dashes
 * come from the browser instead of a repeating gradient.
 *
 * The label flips to the left of the line once today sits in the right-hand fifth of the window —
 * anchored right of the line it would be clipped by the scroll container at the far edge, which is
 * exactly where today lands on the 12-month default.
 */
function TodayLine({ view, label = false }) {
  if (view.todayPct === null || view.todayPct < 0 || view.todayPct > 100) return null;
  const flip = view.todayPct > 80;
  return (
    <div
      style={{
        position: 'absolute',
        left: `${view.todayPct}%`,
        top: 0,
        bottom: 0,
        marginLeft: -1,
        borderLeft: '2px dashed var(--yellow)',
        zIndex: 3,
        pointerEvents: 'none',
      }}
    >
      {label && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            [flip ? 'right' : 'left']: 4,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: 'var(--warn-text)', // Matches .badge-yellow's foreground.
            background: 'var(--surface)',
            padding: '0 2px',
            whiteSpace: 'nowrap',
          }}
        >
          TODAY
        </div>
      )}
    </div>
  );
}

/**
 * The go-live marker. Filled when the date is an actual — a go-live that has happened and one that is
 * planned are the two things a leadership audience most needs to tell apart at a glance.
 */
function GoLiveMarker({ left, delivered, title }) {
  const size = 13;
  return (
    <div
      title={title}
      style={{
        position: 'absolute',
        left: `${left}%`,
        top: '50%',
        width: size,
        height: size,
        marginLeft: -size / 2,
        marginTop: -size / 2,
        transform: 'rotate(45deg)',
        background: delivered ? 'var(--green)' : 'var(--surface)',
        border: `2px solid var(--green)`,
        zIndex: 2,
      }}
    />
  );
}

function ProgressPill({ progress }) {
  if (!progress) return <span style={{ fontSize: 11, color: 'var(--text-light)' }}>—</span>;
  return (
    <div title={PROGRESS_SOURCE_HINT[progress.source]} style={{ paddingRight: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dark)' }}>
        {progress.pct}%
        {progress.source === 'milestones' && (
          <span style={{ color: 'var(--text-light)', fontWeight: 400 }}> ms</span>
        )}
      </div>
      <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, marginTop: 3 }}>
        <div
          style={{
            width: `${progress.pct}%`,
            height: '100%',
            borderRadius: 2,
            background: progress.source === 'tasks' ? 'var(--primary-light)' : 'var(--teal)',
          }}
        />
      </div>
    </div>
  );
}

// ─── PROJECT ROW BAR ─────────────────────────────────────────────────────────

function ProjectBar({ row, view }) {
  const style = STATUS_STYLE[row.projectStatus] ?? STATUS_STYLE.Active;
  const start = view.clamp(row.startDate);
  const end = view.clamp(row.goLive?.date);

  if (start === null && end === null) {
    // Nothing dated anywhere. A pill at today rather than an empty cell: an unscheduled project is a
    // real state worth seeing on a portfolio view, not a rendering gap.
    const left = view.todayPct === null ? 2 : Math.min(Math.max(view.todayPct, 2), 80);
    return (
      <div
        title="No start date, no go-live and nothing dated in the schedule"
        style={{
          position: 'absolute',
          left: `${left}%`,
          top: '50%',
          marginTop: -9,
          height: 18,
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--violet)',
          border: '1px dashed var(--violet)',
          borderRadius: 4,
          background: 'rgba(130,106,175,0.06)',
          whiteSpace: 'nowrap',
        }}
      >
        NOT SCHEDULED
      </div>
    );
  }

  // A start with no end (Ada: a kickoff date and nothing planned behind it yet) draws open-ended
  // rather than guessing a length.
  if (end === null) {
    return (
      <div
        title={`Starts ${fmtFullDate(row.startDate)} — no go-live or schedule end yet`}
        style={{
          position: 'absolute',
          left: `${start}%`,
          width: `${Math.min(8, 100 - start)}%`,
          top: '50%',
          marginTop: -BAR_HEIGHT / 2,
          height: BAR_HEIGHT,
          borderRadius: 4,
          border: `1px dashed ${style.bar}`,
          background: `linear-gradient(90deg, rgba(4,64,96,0.12), transparent)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingRight: 4,
          fontSize: 11,
          color: style.bar,
        }}
      >
        ▸
      </div>
    );
  }

  const left = start === null ? 0 : start;
  const width = Math.max(end - left, 0.4);
  const delivered = !!row.goLive?.actualDate;

  return (
    <>
      <div
        title={`${fmtFullDate(row.startDate)} → ${fmtFullDate(row.goLive.date)}`}
        style={{
          position: 'absolute',
          left: `${left}%`,
          width: `${width}%`,
          top: '50%',
          marginTop: -BAR_HEIGHT / 2,
          height: BAR_HEIGHT,
          borderRadius: 4,
          background: 'var(--border)',
          overflow: 'hidden',
        }}
      >
        {/* Progress fill inside the bar, the same convention ProjectSchedule uses for a task. */}
        <div
          style={{
            width: `${row.progress?.pct ?? 0}%`,
            height: '100%',
            background: delivered ? 'var(--green)' : style.bar,
            opacity: delivered ? 1 : 0.9,
          }}
        />
      </div>
      {/* Every flagged go-live, not only the one the bar ends at — a segmented engagement has several. */}
      {(row.goLiveMilestones?.length ? row.goLiveMilestones : [row.goLive]).map((m, i) => {
        const at = view.clamp(m.date);
        if (at === null) return null;
        return (
          <GoLiveMarker
            key={m.id ?? `derived-${i}`}
            left={at}
            delivered={!!m.actualDate}
            title={
              m.title
                ? `${m.title} — ${fmtFullDate(m.actualDate ?? m.date)}${m.actualDate ? ' (actual)' : ' (planned)'}`
                : `Derived end — ${fmtFullDate(m.date)}`
            }
          />
        );
      })}
    </>
  );
}

// ─── DRILL-DOWN ──────────────────────────────────────────────────────────────

/**
 * Which bands the expanded row draws, and where its milestones hang.
 *
 * Independent toggles rather than one three-way switch (Jeremy, 2026-08-26): workstreams and phases
 * are orthogonal groupings of the same rows, so seeing both at once is a real thing to want.
 * Milestones are an overlay on whichever grouping is showing — workstreams win when both are, since
 * that is the grouping a milestone is most often filed under.
 *
 * State lives per drill-down, not on the page, so opening a second project doesn't inherit the view
 * you set on the first.
 */
function bandRows(row, show) {
  const bands = [];

  if (show.workstreams) {
    for (const w of row.workstreams) {
      bands.push({
        key: `workstream-${w.id}`,
        kind: 'workstream',
        label: w.name,
        start: w.startDate,
        end: w.endDate,
        progress: w.progress,
        meta: `${w.taskCount} task${w.taskCount === 1 ? '' : 's'}${w.milestoneCount ? `, ${w.milestoneCount} milestone${w.milestoneCount === 1 ? '' : 's'}` : ''}`,
        matches: (m) => m.workstreamId === w.id,
      });
    }
  }

  if (show.phases) {
    for (const p of row.phases) {
      bands.push({
        key: `phase-${p.id}`,
        kind: 'phase',
        label: `${p.sequence}. ${p.title}`,
        start: p.startDate,
        end: p.endDate,
        progress: null,
        meta: null,
        // Only when phases are the grouping carrying the milestones — otherwise the same milestone
        // would be drawn twice, once on its workstream and once on its phase.
        matches: show.workstreams ? () => false : (m) => m.phaseId === p.id,
      });
    }
  }

  if (!show.milestones) return { bands, orphans: [] };

  // Milestones whose band isn't drawn — no workstream/phase, or its grouping is switched off. They
  // get a row of their own rather than being silently dropped; on a milestone-only project like
  // Charleston that row IS the schedule.
  const placed = new Set();
  for (const b of bands) {
    for (const m of row.milestones) if (b.matches(m)) placed.add(m.id);
  }
  return { bands, orphans: row.milestones.filter((m) => !placed.has(m.id)) };
}

/** The markers for one band, positioned along it. Titles are on hover only, per Jeremy's ask. */
function MilestoneMarkers({ milestones, view }) {
  return milestones.map((m) => {
    const at = view.clamp(m.date);
    if (at === null) return null;
    return (
      <div
        key={m.id}
        style={{
          position: 'absolute',
          left: `${at}%`,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          lineHeight: 0,
        }}
      >
        <MilestoneGlyph
          status={m.status}
          size={13}
          title={`${m.title} — ${fmtFullDate(m.actualDate ?? m.targetDate)}${m.actualDate ? ' (actual)' : ''}${m.isGoLive ? ' · go-live' : ''}`}
        />
      </div>
    );
  });
}

/**
 * The expanded row: real phase bands and real workstream spans, both straight from the project's own
 * schedule. The mock generated these from a weighted template because it had no schedule to read;
 * a project with no promoted plan now says so instead of inventing seven phases.
 */
function ProjectDetail({ row, view }) {
  // Workstreams by default where there are any (Jeremy's ask), falling through to whatever the
  // project actually has otherwise. A milestone-only project like Charleston would otherwise expand
  // onto "nothing selected" while holding 28 real milestones.
  const [show, setShow] = useState(() => ({
    workstreams: row.workstreams.length > 0,
    phases: !row.workstreams.length && row.phases.length > 0,
    milestones: !row.workstreams.length && !row.phases.length && row.milestones.length > 0,
  }));
  const { bands, orphans } = bandRows(row, show);
  const toggle = (key) => setShow((prev) => ({ ...prev, [key]: !prev[key] }));

  // Rows that will actually draw something. Turning every toggle off is a legitimate thing to do by
  // accident, and an empty panel that looks broken is worse than a line saying what happened.
  const hasRows = bands.length > 0 || orphans.length > 0;

  return (
    <div style={{ background: 'var(--light-bg)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', padding: '12px 0 4px' }}>
        <div style={{ width: LABEL_WIDTH, flexShrink: 0, position: 'sticky', left: 0, paddingLeft: 12, zIndex: 2, background: 'var(--light-bg)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)' }}>
            Schedule
          </div>

          {/* Per-project view settings, inside the drill-down rather than at page level (Jeremy's
              ask) — they describe this project's bands, not the portfolio. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '6px 0 8px' }}>
            {[
              { key: 'workstreams', label: 'Show workstreams', count: row.workstreams.length },
              { key: 'phases', label: 'Show phases', count: row.phases.length },
              { key: 'milestones', label: 'Show milestones', count: row.milestones.length },
            ].map((opt) => (
              <label
                key={opt.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 11,
                  color: opt.count ? 'var(--text-mid)' : 'var(--text-light)',
                }}
              >
                <input
                  type="checkbox"
                  checked={show[opt.key]}
                  disabled={!opt.count}
                  onChange={() => toggle(opt.key)}
                />
                {opt.label}
                <span style={{ color: 'var(--text-light)' }}>({opt.count})</span>
              </label>
            ))}
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 4, lineHeight: 1.5 }}>
            {row.projectManager && <div>PM: {row.projectManager}</div>}
            <div>{row.accountName}</div>
            {row.nextMilestone && (
              <div style={{ marginTop: 4 }}>
                Next: <strong>{row.nextMilestone.title}</strong>
                <br />
                {fmtFullDate(row.nextMilestone.targetDate)}
              </div>
            )}
            {row.counts.milestonesOverdue > 0 && (
              <div style={{ color: 'var(--red)', marginTop: 4 }}>
                ⚠ {row.counts.milestonesOverdue} milestone
                {row.counts.milestonesOverdue === 1 ? '' : 's'} past target
              </div>
            )}
            {SCHEDULING_ENABLED && (
              <div style={{ marginTop: 6 }}>
                <Link to={projectPath('/schedule', row.id)} style={{ fontSize: 11, color: 'var(--primary-light)' }}>
                  Open in Project Schedule →
                </Link>
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: view.minWidth, position: 'relative' }}>
          {!row.workstreams.length && !row.phases.length && !row.milestones.length ? (
            <div style={{ fontSize: 12, color: 'var(--text-mid)', fontStyle: 'italic', padding: '6px 0 14px' }}>
              No phases, workstreams or dated milestones — this project's schedule hasn't been promoted
              from its SOW.
            </div>
          ) : !hasRows ? (
            <div style={{ fontSize: 12, color: 'var(--text-mid)', fontStyle: 'italic', padding: '6px 0 14px' }}>
              Nothing selected — tick a view on the left.
            </div>
          ) : (
            <div style={{ position: 'relative', paddingBottom: 10 }}>
              <GridLines view={view} />
              <TodayLine view={view} />

              {bands.map((b) => {
                const left = view.clamp(b.start);
                const right = view.clamp(b.end);
                const isPhase = b.kind === 'phase';
                const onBand = show.milestones ? row.milestones.filter(b.matches) : [];
                return (
                  <div key={b.key} style={{ position: 'relative', height: 24 }}>
                    {left === null || right === null ? (
                      <span style={{ fontSize: 10, color: 'var(--text-light)', lineHeight: '24px' }}>
                        {b.label} — no dates
                      </span>
                    ) : (
                      <div
                        title={`${b.label}: ${fmtFullDate(b.start)} → ${fmtFullDate(b.end)}${b.meta ? ` (${b.meta})` : ''}`}
                        style={{
                          position: 'absolute',
                          left: `${left}%`,
                          width: `${Math.max(right - left, 0.4)}%`,
                          top: 2,
                          height: 18,
                          borderRadius: 3,
                          background: isPhase ? 'rgba(4,64,96,0.14)' : 'var(--border)',
                          border: isPhase ? '1px solid rgba(4,64,96,0.35)' : 'none',
                          // Not clipped when markers are on: a milestone can legitimately sit on the
                          // band's own end date, and half a diamond reads as a rendering fault.
                          overflow: onBand.length ? 'visible' : 'hidden',
                        }}
                      >
                        {!isPhase && b.progress && (
                          <div
                            style={{
                              width: `${b.progress.pct}%`,
                              height: '100%',
                              background: 'var(--primary-light)',
                              borderRadius: 3,
                            }}
                          />
                        )}
                        <span
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 5,
                            right: 4,
                            fontSize: 9,
                            lineHeight: '18px',
                            fontWeight: 600,
                            color: isPhase ? 'var(--heading)' : 'var(--text-dark)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {b.label}
                          {b.progress ? ` · ${b.progress.pct}%` : ''}
                        </span>
                      </div>
                    )}
                    {/* Positioned against the whole track, not the bar, so a milestone outside its
                        own band's span still lands on its real date. */}
                    <MilestoneMarkers milestones={onBand} view={view} />
                  </div>
                );
              })}

              {orphans.length > 0 && (
                <div style={{ position: 'relative', height: 24 }}>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      lineHeight: '24px',
                      color: 'var(--text-light)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {bands.length ? 'Other milestones' : 'Milestones'}
                  </span>
                  <MilestoneMarkers milestones={orphans} view={view} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────

function PortfolioTimeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [sort, setSort] = useState({ key: 'goLive', dir: 'asc' });
  const [expandedId, setExpandedId] = useState(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiFetch(`/portfolio/timeline?includeClosed=${includeClosed}`));
    } catch (e) {
      setError(e.message || 'Could not load the portfolio timeline.');
    } finally {
      setLoading(false);
    }
  }, [includeClosed]);

  useEffect(() => {
    load();
  }, [load]);

  const view = useMemo(() => buildWindow(data?.window, zoom), [data, zoom]);

  // Scroll today into the middle of the track whenever the axis changes. useLayoutEffect, not
  // useEffect: the scroll has to land before the browser paints, or the "All" zoom flashes 2020
  // before jumping. Keyed on `view` so switching zoom re-centers rather than keeping a stale offset.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = centerTodayScrollLeft(view.todayPct, el.scrollWidth, el.clientWidth);
    if (left !== null) el.scrollLeft = left;
  }, [view]);

  const rows = useMemo(() => {
    const list = [...(data?.projects ?? [])];
    const column = COLUMNS.find((c) => c.key === sort.key);
    if (!column) return list;
    return list.sort((a, b) => {
      const av = column.sortBy(a);
      const bv = column.sortBy(b);
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sort.dir === 'desc' ? -cmp : cmp;
    });
  }, [data, sort]);

  const toggleSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const summary = useMemo(() => {
    const states = new Set(rows.map((r) => r.state).filter(Boolean));
    return {
      states: states.size,
      scheduled: rows.filter((r) => r.counts.phases > 0).length,
      overdue: rows.reduce((n, r) => n + r.counts.milestonesOverdue, 0),
    };
  }, [rows]);

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Portfolio</div>
        <h1 className="page-title">Portfolio Timeline</h1>
        <p className="page-subtitle">
          {loading && !data
            ? 'Loading…'
            : `${rows.length} projects · ${summary.states} states · ${summary.scheduled} with a promoted schedule`}
        </p>
      </div>

      {error && (
        <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--red)' }}>
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            <strong style={{ color: 'var(--red)' }}>Couldn't load the portfolio.</strong> {error}
          </div>
          <button className="btn btn-outline" style={{ marginTop: 12 }} onClick={load}>
            Try again
          </button>
        </div>
      )}

      <div className="card">
        {/* TOOLBAR */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
            paddingBottom: 12,
            marginBottom: 8,
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div style={{ display: 'flex', gap: 4 }}>
            {ZOOMS.map((z) => (
              <button
                key={z.key}
                className={`btn ${zoom === z.key ? 'btn-primary' : 'btn-outline'}`}
                style={{ fontSize: 12, padding: '5px 12px' }}
                onClick={() => setZoom(z.key)}
              >
                {z.label}
              </button>
            ))}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-mid)' }}>
            <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} />
            Show closed projects
          </label>

          <div style={{ display: 'flex', gap: 14, marginLeft: 'auto', fontSize: 11, color: 'var(--text-mid)', alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 11, height: 11, transform: 'rotate(45deg)', border: '2px solid var(--green)' }} />
              Go-live planned
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 11, height: 11, transform: 'rotate(45deg)', background: 'var(--green)' }} />
              Went live
            </span>
            <span title="No go-live milestone is flagged, so the date is inferred from the schedule">
              ~ derived date
            </span>
            <span title="Progress from delivered milestones, not from a promoted task schedule">
              % ms milestone-based
            </span>
            {summary.overdue > 0 && (
              <span style={{ color: 'var(--red)', fontWeight: 600 }}>
                ⚠ {summary.overdue} past target portfolio-wide
              </span>
            )}
          </div>
        </div>

        {/* The whole table scrolls sideways as one; the label columns stay pinned. */}
        <div ref={scrollRef} style={{ overflowX: 'auto' }}>
          {/* HEADER */}
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
            <div
              style={{
                width: LABEL_WIDTH,
                flexShrink: 0,
                display: 'flex',
                position: 'sticky',
                left: 0,
                zIndex: 4,
                background: 'var(--surface)',
              }}
            >
              {COLUMNS.map((c) => (
                <SortTh key={c.key} column={c} sort={sort} onSort={toggleSort} />
              ))}
            </div>
            <div style={{ flex: 1, minWidth: view.minWidth, position: 'relative' }}>
              <TimelineAxis view={view} />
              <TodayLine view={view} label />
            </div>
          </div>

          {!loading && !rows.length && !error && (
            <div style={{ fontSize: 13, color: 'var(--text-mid)', fontStyle: 'italic', padding: '20px 4px' }}>
              No projects to show. Only fixed-fee (POC) engagements appear here — T&amp;M letters of
              authorization and internal time buckets are excluded.
            </div>
          )}

          {rows.map((row) => {
            const expanded = expandedId === row.id;
            const style = STATUS_STYLE[row.projectStatus] ?? STATUS_STYLE.Active;
            const source = GO_LIVE_SOURCE[row.goLive?.source];
            return (
              <div key={row.id}>
                <div
                  onClick={() => setExpandedId(expanded ? null : row.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'stretch',
                    minHeight: ROW_HEIGHT,
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                    background: expanded ? 'rgba(47,147,177,0.06)' : 'transparent',
                  }}
                >
                  <div
                    style={{
                      width: LABEL_WIDTH,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      background: expanded ? 'var(--hover-tint)' : 'var(--surface)',
                    }}
                  >
                    <div style={{ width: COLUMNS[0].width, flexShrink: 0, fontSize: 11, fontWeight: 700, color: 'var(--heading)' }}>
                      {row.state || '—'}
                    </div>
                    <div style={{ width: COLUMNS[1].width, flexShrink: 0, paddingRight: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        {row.ragStatus && (
                          <span
                            title={`RAG status: ${row.ragStatus}`}
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              flexShrink: 0,
                              background: RAG_COLOR[row.ragStatus] ?? 'var(--gray)',
                            }}
                          />
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.name}
                        </span>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-light)' }}>
                        {row.projectManager || 'No PM assigned'}
                        {row.counts.milestonesOverdue > 0 && (
                          <span style={{ color: 'var(--red)' }}> · ⚠ {row.counts.milestonesOverdue}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ width: COLUMNS[2].width, flexShrink: 0 }}>
                      <span className={`badge ${style.badge}`}>{row.projectStatus}</span>
                    </div>
                    <div style={{ width: COLUMNS[3].width, flexShrink: 0, fontSize: 11, color: 'var(--text-mid)' }}>
                      <span title={source?.hint}>
                        {source?.mark}
                        {fmtMonthYear(row.goLive?.date)}
                      </span>
                      {row.goLive?.actualDate && (
                        <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--green)' }}>WENT LIVE</div>
                      )}
                    </div>
                    <div style={{ width: COLUMNS[4].width, flexShrink: 0 }}>
                      <ProgressPill progress={row.progress} />
                    </div>
                  </div>

                  <div style={{ flex: 1, minWidth: view.minWidth, position: 'relative' }}>
                    <GridLines view={view} />
                    <TodayLine view={view} />
                    <ProjectBar row={row} view={view} />
                  </div>
                </div>

                {expanded && <ProjectDetail row={row} view={view} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PortfolioTimeline;
export { bandRows, buildWindow, COLUMNS, toDay };
